import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';

const Login        = lazy(() => import('./pages/auth/Login'));
const Register     = lazy(() => import('./pages/auth/Register'));
const Home         = lazy(() => import('./pages/home/Home'));
const Specialties  = lazy(() => import('./pages/home/Specialties'));
const DoctorList   = lazy(() => import('./pages/home/DoctorList'));
const DoctorDetail = lazy(() => import('./pages/home/DoctorDetail'));
const BookAppt     = lazy(() => import('./pages/appointment/BookAppt'));
const Appointments = lazy(() => import('./pages/appointment/Appointments'));
const AppointmentDetail = lazy(() => import('./pages/appointment/AppointmentDetail'));
const Records      = lazy(() => import('./pages/records/Records'));
const Followup     = lazy(() => import('./pages/followup/Followup'));
const FollowupDetail = lazy(() => import('./pages/followup/FollowupDetail'));
const Vitals       = lazy(() => import('./pages/vitals/Vitals'));
const Chat         = lazy(() => import('./pages/chat/Chat'));
const VideoCall    = lazy(() => import('./pages/chat/VideoCall'));
const Profile      = lazy(() => import('./pages/profile/Profile'));

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;
  if (!user)   return <Navigate to="/login" replace />;
  return children;
}

function LangWrapper({ children }) {
  const { i18n } = useTranslation();
  const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  React.useEffect(() => {
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language, dir]);
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <LangWrapper>
        <Toaster position="top-center" toastOptions={{ duration: 3000 }} />
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>}>
          <Routes>
            <Route path="/login"    element={<Login />} />
            <Route path="/register" element={<Register />} />

            <Route path="/" element={<RequireAuth><Layout /></RequireAuth>}>
              <Route index element={<Home />} />
              <Route path="specialties"          element={<Specialties />} />
              <Route path="doctors"              element={<DoctorList />} />
              <Route path="doctors/:id"          element={<DoctorDetail />} />
              <Route path="book/:doctorId"       element={<BookAppt />} />
              <Route path="appointments"         element={<Appointments />} />
              <Route path="appointments/:id"     element={<AppointmentDetail />} />
              <Route path="records"              element={<Records />} />
              <Route path="followup"             element={<Followup />} />
              <Route path="followup/:id"         element={<FollowupDetail />} />
              <Route path="vitals"               element={<Vitals />} />
              <Route path="chat"                 element={<Chat />} />
              <Route path="chat/:userId"         element={<Chat />} />
              <Route path="video/:apptId"        element={<VideoCall />} />
              <Route path="profile"              element={<Profile />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </LangWrapper>
    </BrowserRouter>
  );
}
